/**
 * A read a screen repeats while it is open: the read model of a board, a list, the session inbox.
 *
 * - A conditional read names the revision on screen; a `not_modified` commits nothing. Every `safetyReadMs` the read
 *   goes with no validator, so a revision that failed to move cannot hold old data for ever.
 * - A background refresh keeps the last ready value: the status stays `ready`, `refreshing` says one is on the way,
 *   and a failed refresh leaves the value with its `failure` beside it. Only the first read can end in `failed`.
 * - A write's answer lands at once (`land`). A read sent before it may have read the rows before that write, so its
 *   answer is dropped; the next tick reads again from the landed revision.
 * - Revival: a mount takes what the last mount of the same principal, restaurant and query kept (Identity's screen
 *   memory, dropped on any change of them), shows it as `revived` and refreshes behind it.
 *
 * Framework-free: a screen reads it through `subscribe`/`getSnapshot` and ticks it from its own clock (`useLiveRefresh`).
 */
import type { PanelScreenMemoryLease } from '../foundation/runtime-port';

/** @public Three minutes between reads with no validator, the cadence of the staff boards. */
export const POLLED_READ_SAFETY_MS = 180_000;

export type PolledAnswer<Value> =
  | Readonly<{ kind: 'current'; value: Value; entityTag: string | null }>
  | Readonly<{ kind: 'not_modified' }>;

interface PolledReadSnapshot<Value> {
  /** `failed` only when no value has ever been read. */
  readonly status: 'loading' | 'ready' | 'failed';
  readonly value: Value | null;
  /** The last read's failure, kept until a read answers. */
  readonly failure: unknown;
  /** A read is on the wire behind the value on screen. */
  readonly refreshing: boolean;
  /** The value came from the screen memory and no read has answered yet. */
  readonly revived: boolean;
  readonly entityTag: string | null;
}

/** What the screen memory keeps of a polled read. */
interface PolledReadMemory<Value> {
  readonly value: Value;
  readonly entityTag: string | null;
  readonly lastUnconditionalReadAt: number;
}

export interface PolledReadOptions<Value> {
  /** One read, with the validator to send (null: no validator). A read without ETags always answers `current` with a null tag. */
  readonly read: (entityTag: string | null) => Promise<PolledAnswer<Value>>;
  readonly safetyReadMs?: number;
  /** This mount's lease on the screen memory, when the screen remembers. */
  readonly memory?: PanelScreenMemoryLease<PolledReadMemory<Value>> | null;
  /** Runs on every value that lands, read or written, before it is published. A throw refuses a read's value as a failed read. */
  readonly accept?: (value: Value) => void;
  readonly now?: () => number;
}

export interface PolledRead<Value> {
  subscribe(listener: () => void): () => void;
  getSnapshot(): PolledReadSnapshot<Value>;
  /** Reads now; one read at a time, so a refresh during a read joins it. `unconditional` goes with no validator. */
  refresh(request?: Readonly<{ unconditional?: boolean }>): Promise<void>;
  /** A write's answer: the value at once, and every read sent before it is dropped. */
  land(value: Value, entityTag?: string | null): void;
  /** Whatever was asked changed (a wider window): reads sent before it are dropped and a new read goes now. */
  invalidate(): Promise<void>;
  /** The mount leaves: keeps the value in the screen memory. No answer lands afterwards. */
  dispose(): void;
}

export function createPolledRead<Value>(options: PolledReadOptions<Value>): PolledRead<Value> {
  const now = options.now ?? Date.now;
  const safetyReadMs = options.safetyReadMs ?? POLLED_READ_SAFETY_MS;
  const recalled = options.memory?.recalled ?? null;
  const listeners = new Set<() => void>();
  let lastUnconditionalReadAt = recalled?.lastUnconditionalReadAt ?? Number.NEGATIVE_INFINITY;
  let snapshot: PolledReadSnapshot<Value> = recalled === null
    ? Object.freeze({ status: 'loading', value: null, failure: null, refreshing: false, revived: false, entityTag: null })
    : Object.freeze({ status: 'ready', value: recalled.value, failure: null, refreshing: false, revived: true, entityTag: recalled.entityTag });
  let sequence = 0;
  /** A read sent at or before this sequence is older than what is on screen. */
  let droppedThrough = 0;
  let inFlight: Promise<void> | null = null;
  let disposed = false;

  const publish = (patch: Partial<PolledReadSnapshot<Value>>) => {
    snapshot = Object.freeze({ ...snapshot, ...patch });
    for (const listener of [...listeners]) listener();
  };

  const readOnce = (unconditional: boolean): Promise<void> => {
    const sent = !unconditional && snapshot.entityTag !== null && now() - lastUnconditionalReadAt < safetyReadMs
      ? snapshot.entityTag : null;
    const startedAt = now();
    const mine = ++sequence;
    publish({ refreshing: true });
    let settled: Promise<void>;
    const finish = () => { if (inFlight === settled) inFlight = null; };
    settled = options.read(sent).then((answer) => {
      finish();
      if (disposed) return;
      if (mine <= droppedThrough) { publish({ refreshing: inFlight !== null }); return; }
      if (answer.kind === 'not_modified') {
        publish({ refreshing: false, revived: false, failure: null, status: snapshot.value === null ? snapshot.status : 'ready' });
        return;
      }
      try { options.accept?.(answer.value); }
      catch (failure) {
        publish({ status: snapshot.value === null ? 'failed' : 'ready', failure, refreshing: false });
        return;
      }
      if (sent === null) lastUnconditionalReadAt = startedAt;
      publish({ status: 'ready', value: answer.value, entityTag: answer.entityTag, failure: null, refreshing: false, revived: false });
    }, (failure: unknown) => {
      finish();
      if (disposed) return;
      if (mine <= droppedThrough) { publish({ refreshing: inFlight !== null }); return; }
      publish({ status: snapshot.value === null ? 'failed' : 'ready', failure, refreshing: false });
    });
    inFlight = settled;
    return settled;
  };

  return {
    subscribe(listener) {
      listeners.add(listener);
      return () => { listeners.delete(listener); };
    },
    getSnapshot: () => snapshot,
    refresh(request) {
      if (disposed) return Promise.resolve();
      if (inFlight !== null) return inFlight;
      return readOnce(request?.unconditional === true);
    },
    land(value, entityTag = null) {
      if (disposed) return;
      droppedThrough = sequence;
      // A write's answer is a whole board read just now: it counts as the read with no validator.
      lastUnconditionalReadAt = now();
      options.accept?.(value);
      publish({ status: 'ready', value, entityTag, failure: null, revived: false, refreshing: false });
    },
    invalidate() {
      if (disposed) return Promise.resolve();
      droppedThrough = sequence;
      inFlight = null;
      return readOnce(true);
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      if (snapshot.value !== null) {
        options.memory?.keep({ value: snapshot.value, entityTag: snapshot.entityTag, lastUnconditionalReadAt });
      }
      listeners.clear();
    },
  };
}
